import { useNavigate } from 'react-admin';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip } from '@mui/material';
import { Folder, PictureAsPdf, Description, InsertDriveFile } from '@mui/icons-material';
import { FileRecord } from './constants';

interface EmployeeDocumentsProps {
  records: FileRecord[];
}

const getFileIcon = (mimeType: string) => {
  if (mimeType === 'application/pdf') return <PictureAsPdf fontSize="small" />;
  if (mimeType.startsWith('image/')) return <Description fontSize="small" />;
  return <InsertDriveFile fontSize="small" />;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const EmployeeDocuments = ({ records }: EmployeeDocumentsProps) => {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Folder color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Employee Files</Typography>
        <Chip label={`${records.length} files`} size="small" sx={{ ml: 1 }} />
      </Box>
      {records.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Subsystem</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sensitivity</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((f: FileRecord) => (
              <TableRow key={f.id} hover sx={{ cursor: 'pointer' }} onClick={() => { navigate(`/files/${f.id}/show`); }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getFileIcon(f.mimeType ?? '')}
                    <Typography variant="body2">{f.fileName}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Chip label={f.documentType} size="small" variant="outlined" /></TableCell>
                <TableCell>{f.subsystem}</TableCell>
                <TableCell>{formatSize(f.size ?? 0)}</TableCell>
                <TableCell><Chip label={f.status} size="small" color={f.status === 'Active' ? 'success' : f.status === 'Deleted' ? 'error' : 'default'} /></TableCell>
                <TableCell>
                  <Chip
                    label={f.legislation?.sensitivityLevel || '—'}
                    size="small"
                    color={
                      f.legislation?.sensitivityLevel === 'Restricted' ? 'error' :
                      f.legislation?.sensitivityLevel === 'Confidential' ? 'warning' :
                      f.legislation?.sensitivityLevel === 'Internal' ? 'info' : 'default'
                    }
                  />
                </TableCell>
                <TableCell>{f.metadata?.createdAt ? new Date(f.metadata.createdAt).toLocaleDateString('en-GB') : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No files found for this employee</Typography>
      )}
    </Box>
  );
};
